// 給与：日給乗務員の「支給対象日数」の材料（純関数・スプリント02／F2）。
// 仕様: docs/spec.md §4 F2・確定事項 D-2・設計判断 J-2。
//
// ここが F2 の核心（外すと意味が無い）:
//   日給の日数は「収支の稼働日を出発点にするが、土日祝に走った日も 1 日として数える」＝**暦日ベース**。
//   収支の稼働日は operations.js の bizDaysBetween（積日→卸日の**営業日**ユニオン・土日祝除外）で、
//   これは収支の稼働日と車両経費の配賦分母が使っている。**書き換えると収支の数字が動く**ので触らない（J-2）。
//   そこで、同じ「積日→卸日の日ユニオン」を**土日祝で絞らない版**としてここに新設する。
//   したがってこのファイルは祝日・営業日の判定を一切持たない（持ったら暦日ベースではなくなる）。
//
// 既存 bizDaysBetween が持つ防御はそのまま引き継ぐ:
//   - 積日が読めなければ空配列（日数を捏造しない）
//   - 卸日が読めない／積日より前（データ不正）なら積日 1 日へフォールバック
//   - 上限 SPAN_GUARD 日でループを打ち切る（巨大配列・無限ループを作らせない）
import { normTacho } from '../analytics/operations.js'

export const MAX_DAYS = 31 // 支給対象日数の上限（暦月に存在しうる日数）
export const SPAN_GUARD = 60 // 1 便が跨げる日数の上限。既存 bizDaysBetween と同じ値

const pad2 = (n) => String(n).padStart(2, '0')
const isoYMD = (dt) => `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`
const normCode = (c) => String(c == null ? '' : c).trim().replace(/^0+(?=\d)/, '')

// 'YYYY-M-D' 系の文字列から 'YYYY-MM-DD' を取り出す（normTrip の dLoad/dUnload は既にこの形）。
const asISO = (v) => {
  const m = String(v == null ? '' : v).match(/(\d{4})-(\d{1,2})-(\d{1,2})/)
  return m ? `${m[1]}-${pad2(+m[2])}-${pad2(+m[3])}` : ''
}

/**
 * 積日→卸日の**暦日**を 'YYYY-MM-DD' 配列で返す（土日祝を除外しない。D-2）。
 * 収支の bizDaysBetween と同じ入力・同じガードで、営業日の絞り込みだけを外したもの。
 */
export function calDaysBetween(loadStr, unloadStr) {
  const a = asISO(loadStr); if (!a) return []
  const b = asISO(unloadStr) || a
  const s = new Date(a + 'T00:00:00'), e = new Date(b + 'T00:00:00')
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return [a] // データ不正は積日 1 日（暦日版は土日祝でも落とさない）
  const out = []; const c = new Date(s); let g = 0
  while (c <= e && g < SPAN_GUARD) { out.push(isoYMD(c)); c.setDate(c.getDate() + 1); g++ }
  return out
}

/**
 * 乗務員コード → その月の暦日ユニオン（'YYYY-MM-DD' 昇順）。
 * @param {object[]} normalizedOwnTrips normTrip 済みの**自社便**（対象月ぶん）。dLoad/dUnload/driverCode を見る
 * 同一乗務員・同一日の複数便はユニオンで 1 日に集約する（収支の稼働日と同じ数え方）。
 */
export function calendarDaysByDriver(normalizedOwnTrips) {
  const sets = {}
  for (const t of (normalizedOwnTrips || [])) {
    const days = calDaysBetween(t.dLoad, t.dUnload)
    if (!days.length) continue
    const code = normCode(t.driverCode)
    const s = sets[code] || (sets[code] = new Set())
    for (const d of days) s.add(d)
  }
  const out = {}
  for (const k in sets) out[k] = [...sets[k]].sort()
  return out
}

/**
 * 乗務員コード → その月のデジタコ運行日数（**運行開始日**のユニーク数。参考表示用）。
 * デジタコの開始日は [月, 日]（年を持たない）ため、収支ビューの monthOf と同じく月だけで絞る。
 * 額には一切効かない（あくまで経理が日数を決めるときの参考値）。
 */
export function tachoRunDays(tachos, month) {
  const mm = Number(String(month || '').slice(5, 7))
  if (!mm) return {}
  const sets = {}
  for (const t of (tachos || []).map(normTacho)) {
    if (!t.driverCode || !t.sd || t.sd[0] !== mm) continue
    const s = sets[t.driverCode] || (sets[t.driverCode] = new Set())
    s.add(t.sd[0] * 100 + t.sd[1])
  }
  const out = {}
  for (const k in sets) out[k] = sets[k].size
  return out
}

/**
 * 入力・保存値の正規化。未入力（空・数値でない）は null＝「日数未入力」を保ち、0 円と断定しない。
 * 数値は整数へ四捨五入し 0〜MAX_DAYS へクランプする（壊れた保存値で額が暴れないようにする）。
 */
export function normDays(v) {
  if (v == null || v === '' || typeof v === 'boolean') return null
  const n = Number(v)
  if (!isFinite(n)) return null
  return Math.min(MAX_DAYS, Math.max(0, Math.round(n)))
}

/**
 * 入力欄の値を正規化しつつ「0〜MAX_DAYS の整数へ丸めたかどうか」を返す（02 eval B2）。
 * normDays と同じ結果を返し、加えて clamped を添えるだけ（正規化の口を 2 つに分けない）。
 * clamped=true のとき、呼び側は**入力欄の表示も days へ書き戻す責任がある**。
 * 「画面に出ている日数 × 日額 ＝ 画面に出ている額」は常に成り立たせる。
 * 未入力（空・非数値）は clamped=false＝丸めではない（0 円と断定しない・打鍵中に書き換えない）。
 */
export function normDaysInput(v) {
  const days = normDays(v)
  if (days == null) return { days: null, clamped: false }
  const n = Number(v)
  return { days, clamped: !(isFinite(n) && n === days) }
}

const MONTH_RE = /^\d{4}-\d{2}$/

/**
 * 全月マップ（{ 'YYYY-MM': { 乗務員コード: 日数 } }）へ 1 件の編集を当てる（02 eval B1）。
 *
 * **書き先の月は必ず引数で受け取る。「いま画面に出ている月」を参照しない。**
 * 入力欄に文字を打った直後に月を切り替えると、その入力欄の change イベントやデバウンス保存が
 * **表示月が変わったあとで**発火する。そこで現在月を見て書くと、切替先の月の保存値を
 * 黙って上書きしてしまう（ラウンド1 の致命バグ）。月を値と一緒に持ち回ることでしか防げない。
 *
 * @returns {{days:number|null, clamped:boolean, month:string, code:string, applied:boolean}}
 */
export function applyDayEdit(all, month, code, value) {
  const res = normDaysInput(value)
  const m = String(month == null ? '' : month)
  const c = normCode(code)
  if (!all || typeof all !== 'object' || !MONTH_RE.test(m) || !c) {
    return { ...res, month: m, code: c, applied: false } // 不正な月キー・空コードで保管庫を汚さない
  }
  const bucket = all[m] || (all[m] = {})
  if (res.days == null) delete bucket[c]
  else bucket[c] = res.days
  return { ...res, month: m, code: c, applied: true }
}
